<template>
  <div class="flex mb-12 certification-form">
    <div
      id="certification-form-left-panel"
      class="w-3/5 certification-form__left-panel"
    >
      <vs-alert
        v-if="majorVersionImpactWarning"
        color="warning"
        icon-pack="feather"
        icon="icon-alert-triangle"
        class="mb-5"
      >
        {{ majorVersionImpactWarning }}
      </vs-alert>
      <vs-alert v-if="isPublishedVersion" color="warning" class="mb-5">
        Saving with status <strong>Published</strong> will require a mandatory
        change note
      </vs-alert>
      <div class="flex flex-wrap certification-fields">
        <div class="w-full flex certification-form__primary-fields">
          <div class="pb-8 certification-form__badge-field">
            <label class="vs-input--label">Certification badge</label>
            <div
              class="certification-form__badge-preview"
              :class="{
                'certification-form__badge-preview--empty': !badgePreviewUrl,
              }"
            >
              <img
                v-if="badgePreviewUrl"
                :src="badgePreviewUrl"
                alt="Certification badge preview"
              />
              <span v-else aria-hidden="true">+</span>
            </div>
            <input
              ref="badgeFileInput"
              class="certification-form__badge-file-input"
              type="file"
              accept="image/*"
              :disabled="isBadgeUploading || isCreatingCertification"
              @change="onBadgeFileSelected"
            />
            <vs-button
              class="mt-3 certification-form__badge-select"
              color="primary"
              type="border"
              :disabled="isBadgeUploading || isCreatingCertification"
              @click="triggerBadgeFilePicker"
            >
              {{ badgeSelectionButtonLabel }}
            </vs-button>
            <small
              v-if="badgeUploadError"
              class="field-error-msg certification-form__badge-error"
            >
              {{ badgeUploadError }}
            </small>
          </div>

          <div class="flex-1 certification-form__identity-fields">
            <div class="w-full pb-6 certification-form__name-field">
              <label class="vs-input--label">Name *</label>
              <vs-input
                :class="{
                  'input-error':
                    isCertificationIdentityEditable &&
                    certificationIdentityFieldErrors.certificationName,
                }"
                class="w-full"
                v-model="form.certificationName"
                :disabled="!isCertificationIdentityEditable"
                @blur="
                  isCreationMode
                    ? validateCreationIdentityFields()
                    : validateEditIdentityFields()
                "
              />
              <small
                v-if="
                  isCertificationIdentityEditable &&
                  certificationIdentityFieldErrors.certificationName
                "
                class="field-error-msg"
              >
                {{ certificationIdentityFieldErrors.certificationName }}
              </small>
            </div>

            <div class="w-full pb-8 certification-form__short-name-field">
              <label class="vs-input--label">Short Name *</label>
              <vs-input
                :class="{
                  'input-error':
                    isCertificationIdentityEditable &&
                    certificationIdentityFieldErrors.certificationShortName,
                }"
                class="w-full"
                v-model="form.certificationShortName"
                :disabled="!isCertificationIdentityEditable"
                @blur="
                  isCreationMode
                    ? validateCreationIdentityFields()
                    : validateEditIdentityFields()
                "
              />
              <small
                v-if="
                  isCertificationIdentityEditable &&
                  certificationIdentityFieldErrors.certificationShortName
                "
                class="field-error-msg"
              >
                {{ certificationIdentityFieldErrors.certificationShortName }}
              </small>
            </div>
          </div>
        </div>

        <div class="w-1/2 pr-3 pb-8 certification-form__learning-area-field">
          <label class="vs-input--label"
            >Learning Area{{ isCertificationIdentityEditable ? ' *' : '' }}</label
          >
          <v-select
            :class="{
              'certification-form__learning-area-select--error':
                isCertificationIdentityEditable &&
                certificationIdentityFieldErrors.learningArea,
            }"
            v-model="form.learningArea"
            :options="learningAreaOptions"
            :reduce="(option) => option.value"
            :disabled="!isCertificationIdentityEditable"
            @input="clearLearningAreaError"
          />
          <small
            v-if="
              isCertificationIdentityEditable &&
              certificationIdentityFieldErrors.learningArea
            "
            class="field-error-msg"
          >
            {{ certificationIdentityFieldErrors.learningArea }}
          </small>
        </div>

        <div class="w-1/2 pl-3 pb-8 certification-form__difficulty-field">
          <label class="vs-input--label">Difficulty</label>
          <v-select
            v-model="form.difficulty"
            :options="difficultyOptions"
            :reduce="(option) => option.value"
            :disabled="!isCertificationIdentityEditable"
          />
        </div>

        <div class="w-1/3 pr-3 pb-8 certification-form__attempts-field">
          <label class="vs-input--label"
            ># of attempts{{ isCreationMode ? '' : ' *' }}</label
          >
          <vs-input
            :class="{ 'input-error': voucherFieldErrors.max_attempts }"
            class="w-full"
            v-model="form.maxAttempts"
            type="number"
          />
          <small v-if="voucherFieldErrors.max_attempts" class="field-error-msg">
            {{ voucherFieldErrors.max_attempts }}
          </small>
        </div>

        <div
          class="w-1/3 px-3 pb-8 certification-form__next-attempt-time-field"
        >
          <label class="vs-input--label"
            >Next attempt time (hrs){{ isCreationMode ? '' : ' *' }}</label
          >
          <vs-input
            :class="{
              'input-error':
                voucherFieldErrors.time_after_first_attempt_in_minutes,
            }"
            class="w-full"
            v-model="form.nextAttemptTime"
            type="number"
          />
          <small
            v-if="voucherFieldErrors.time_after_first_attempt_in_minutes"
            class="field-error-msg"
          >
            {{ voucherFieldErrors.time_after_first_attempt_in_minutes }}
          </small>
        </div>

        <div
          class="w-1/3 pl-3 pb-8 certification-form__valid-for-days-field"
        >
          <label class="vs-input--label"
            >Valid for (days){{ isCreationMode ? '' : ' *' }}</label
          >
          <vs-input
            :class="{ 'input-error': voucherFieldErrors.valid_for_in_days }"
            class="w-full"
            v-model="form.validForInDays"
            type="number"
          />
          <small
            v-if="voucherFieldErrors.valid_for_in_days"
            class="field-error-msg"
          >
            {{ voucherFieldErrors.valid_for_in_days }}
          </small>
        </div>

        <div class="w-full pb-8 certification-form__accredible-group-id-field">
          <label class="vs-input--label">Accredible group id</label>
          <vs-input
            class="w-full"
            v-model="form.accredibleGroupId"
          />
        </div>

        <div
          v-if="!isCreationMode"
          class="w-1/2 pr-3 pb-8 certification-form__retires-at-field"
        >
          <label class="vs-input--label">Retires on</label>
          <vs-input class="w-full" type="date" v-model="form.retiresAt" />
          <small class="field-hint-msg">
            Leave empty if this version is not retiring.
          </small>
        </div>

        <div class="w-full pb-8 certification-form__learning-paths-field">
          <label class="vs-input--label">Learning paths</label>
          <v-select
            class="certification-form__learning-paths-select"
            multiple
            :value="form.learningPaths"
            :options="lpathsOptions"
            :filterable="false"
            :reduce="(option) => option.value"
            @search="(search, loading) => onSearchLPaths(search, loading, { status: 'published' })"
            @input="setLearningPaths"
          >
            <template slot="no-options">type to search</template>
            <template #selected-option="option">
              <span
                class="certification-form__learning-path-selected-option"
                :title="option.label"
              >
                {{ option.label }}
              </span>
            </template>
          </v-select>
          <div
            v-if="assignedLearningPaths.length"
            class="flex mt-2 text-grey certification-form__learning-path-links"
          >
            <span>View in <i>my.ine</i>:</span>
            <span
              v-for="learningPath in assignedLearningPaths"
              :key="learningPath.id"
              class="flex ml-1 certification-form__learning-path-actions"
            >
              <a
                class="certification-form__learning-path-link"
                target="_blank"
                rel="noopener noreferrer"
                :href="buildLearningPathUrlById(learningPath.id)"
              >
                {{ learningPath.label }}
              </a>
              <vx-tooltip text="Copy URL">
                <button
                  type="button"
                  class="ml-1 cursor-pointer certification-form__learning-path-copy"
                  :aria-label="`Copy URL for ${learningPath.label}`"
                  @click="copyLearningPathUrl(learningPath.id)"
                >
                  <feather-icon icon="CopyIcon" svgClasses="h-3 w-3" />
                </button>
              </vx-tooltip>
            </span>
          </div>
        </div>

        <div class="w-full certification-recommendations-text-field">
          <label class="vs-input--label">Recommendations text</label>
          <markdown-editor
            v-model="form.recommendationsMd"
            link-insert-mode="html"
            :options="{
              hideModeSwitch: true,
              previewStyle: 'tab',
            }"
            height="400px"
          />
        </div>

        <div class="w-full certification-instructions-text-field mt-5">
          <label class="vs-input--label">Instructions text</label>
          <markdown-editor
            v-model="form.instructionsMd"
            link-insert-mode="html"
            :options="{
              hideModeSwitch: true,
              previewStyle: 'tab',
            }"
            height="400px"
          />
        </div>
      </div>
      <certification-change-notes-accordion
        ref="changeNotesAccordion"
        v-if="certificationChangeNotesEnabled && !isCreationMode && certificationId && certificationVersion.id"
        :certificationId="certificationId"
        :certificationVersionId="certificationVersion.id"
        :certificationExamId="certificationExam && certificationExam.id"
      />
    </div>

    <div class="w-2/5 pl-10 certification-form__right-panel">
      <div class="flex mb-2 -mx-2">
        <div class="w-1/2 px-2 created-by-name">
          <label class="vs-input--label">Created by</label>
          <vs-input class="w-full" disabled :value="displayCreatedByName" />
        </div>
        <div class="w-1/2 px-2 created-date">
          <label class="vs-input--label">Created on</label>
          <vs-input class="w-full" disabled :value="displayCreated | dateFormat" />
        </div>
      </div>
      <div class="flex mb-10 -mx-2">
        <div class="w-1/2 px-2 modified-by-name">
          <label class="vs-input--label">Modified by</label>
          <vs-input class="w-full" disabled :value="modifiedByName" />
        </div>
        <div class="w-1/2 px-2 modified-date">
          <label class="vs-input--label">Modified on</label>
          <vs-input class="w-full" disabled :value="modified | dateFormat" />
        </div>
      </div>

      <div class="content-status mb-5">
        <label class="vs-input--label">Status</label>
        <v-select
          v-model="status"
          :clearable="false"
          :options="statusOptions"
          :reduce="(option) => option.value"
          :disabled="isCreationMode || isParentCertificationDraft || !isAdminUser"
        />
      </div>

      <div class="flex -mx-2 certification-form__actions">
        <vs-button
          class="w-full mx-2"
          color="primary"
          icon-pack="feather"
          :disabled="
            isCreationMode
              ? isCreatingCertification || !hasPendingCertificationChanges
              : isSaving || !hasPendingGeneralInfoChanges
          "
          :loading="isCreationMode ? isCreatingCertification : isSaving"
          @click="isCreationMode ? $emit('save-creation') : saveCertification()"
        >
          {{
            isCreationMode
              ? isCreatingCertification
                ? certificationFormActionLabels.CREATING
                : certificationFormActionLabels.CREATE_AS_DRAFT
              : isSaving
              ? certificationFormActionLabels.SAVING
              : certificationFormActionLabels.SAVE
          }}
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import CONSTANTS from '@/constants'
import {
  CERTIFICATION_FORM_ACTION_LABELS,
  CERTIFICATION_STATUS_OPTIONS,
  CERTIFICATION_VOUCHER_FIELD_MAPPINGS,
} from '@/constants/certifications'
import { ROLE_ADMIN } from '@/constants/roles'
import {
  getPublishValidationErrors,
  mapPublishValidationErrorsToLabels,
  adaptPatchCertificationVersionToApi,
  VOUCHER_KEYS,
} from '@/services/adapters/certificationAdapter'
import {
  areCertificationFormSnapshotValuesEqual,
  buildCertificationFormChangeSnapshot,
  buildCertificationFormSnapshot,
  buildCertificationIdentityPatch,
  buildCertificationVersionUpdatePayload,
  buildCreationCertificationPayload,
  buildCreationCertificationVersionPayload,
  buildPersistedCertificationVersion,
  getCertificationIdentityValidationErrors,
  mapCertificationIdentityBackendErrors as mapIdentityBackendErrors,
  normalizeCertificationDateInputValue,
  normalizeCertificationFormDate,
  normalizeCertificationLearningPaths,
  normalizeCertificationMarkdownValue,
  stringifyCertificationFormValue,
} from './certificationForm.helpers'
import { convertHoursToMinutes, convertMinutesToHours } from '@/utils/utils'
import { buildLearningPathUrlById } from '@/utils/navigationHelper'
import { searchLPaths } from '@/utils/mixins'
import MarkdownEditor from '@/components/markdownEditor'
import CertificationChangeNotesAccordion from './components/CertificationChangeNotesAccordion.vue'

const buildEmptyForm = () => ({
  certificationName: '',
  certificationShortName: '',
  learningArea: null,
  difficulty: null,
  maxAttempts: '',
  nextAttemptTime: '',
  voucherStartDate: null,
  validForInDays: '',
  accredibleGroupId: '',
  retiresAt: '',
  learningPaths: [],
  recommendationsMd: '',
  instructionsMd: '',
})

export default {
  name: 'certification-form',
  components: {
    'v-select': vSelect,
    MarkdownEditor,
    CertificationChangeNotesAccordion,
  },
  mixins: [searchLPaths],
  props: {
    certificationVersion: {
      type: Object,
      default: null,
    },
    certificationExam: {
      type: Object,
      default: null,
    },
    isCreationMode: {
      type: Boolean,
      default: false,
    },
    isCreatingCertification: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isParentCertificationDraft: {
      type: Boolean,
      default: false,
    },
    creationCreatedAt: {
      type: String,
      default: '',
    },
    creationCreatedByName: {
      type: String,
      default: '',
    },
    requestChangeNote: {
      type: Function,
      required: true,
    },
    pendingBadgeUpload: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: buildEmptyForm(),
      certificationFormActionLabels: CERTIFICATION_FORM_ACTION_LABELS,
      originalChangeSnapshot: null,
      persistedCertificationVersion: null,
      modified: '',
      created: '',
      createdByName: '',
      modifiedByName: '',
      isSaving: false,
      status: CONSTANTS.CERTIFICATION_STATUSES.DRAFT,
      certificationIdentityFieldErrors: {},
      voucherFieldErrors: {},
      selectedBadgeFile: null,
      localBadgePreviewUrl: '',
      isBadgeUploading: false,
      badgeUploadError: '',
      badgeOwnerCertificationId: null,
      badgeUploadRequestId: 0,
      consumedPendingBadgeUpload: null,
    }
  },
  computed: {
    ...mapGetters('featureFlags', ['featureFlagsMap']),
    ...mapGetters('app', ['getSelectedCompany']),
    ...mapGetters('lareas', ['getLAreas', 'getLAreasEntries']),
    ...mapGetters('courses', ['getCourseDifficulties']),
    ...mapGetters('users', ['currentUserRoles']),
    certificationChangeNotesEnabled() {
      return Boolean(
        this.featureFlagsMap && this.featureFlagsMap.certificationChangeNotes
      )
    },
    learningAreaOptions() {
      return Array.isArray(this.getLAreas) ? [...this.getLAreas] : []
    },
    isAdminUser() {
      return (this.currentUserRoles || []).includes(ROLE_ADMIN)
    },
    displayCreated() {
      return this.isCreationMode ? this.creationCreatedAt : this.created
    },
    displayCreatedByName() {
      return this.isCreationMode
        ? this.creationCreatedByName
        : this.createdByName
    },
    isCertificationIdentityEditable() {
      if (this.isCreationMode) return true
      return Boolean(
        this.certificationVersion &&
          this.certificationVersion.certification &&
          this.certificationVersion.certification.status ===
            CONSTANTS.CERTIFICATION_STATUSES.DRAFT
      )
    },
    difficultyOptions() {
      // TODO check if difficulties should be fetched from an endpoint instead of being derived from courses difficulties, as they might differ
      const options = Array.isArray(this.getCourseDifficulties)
        ? [...this.getCourseDifficulties]
        : []
      ;[this.form.difficulty].filter(Boolean).forEach((value) => {
        if (options.some((option) => option.value === value)) return
        options.push({
          label: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
          value,
        })
      })
      return options
    },
    assignedLearningPaths() {
      return normalizeCertificationLearningPaths(this.form.learningPaths).map(
        (learningPathId) => {
          const option = this.lpathsOptions.find(
            (lpathOption) => lpathOption.value === learningPathId
          )

          return {
            id: learningPathId,
            label: (option && option.label) || learningPathId,
          }
        }
      )
    },
    isPublishedVersion() {
      return (
        !this.isCreationMode &&
        this.certificationVersion &&
        this.certificationVersion.status ===
          CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED
      )
    },
    statusSource() {
      if (this.isCreationMode) return CONSTANTS.CERTIFICATION_STATUSES.DRAFT
      return (
        (this.persistedCertificationVersion &&
          this.persistedCertificationVersion.status) ||
        (this.certificationVersion && this.certificationVersion.status) ||
        CONSTANTS.CERTIFICATION_STATUSES.DRAFT
      )
    },
    isPublishingStatus() {
      return this.status === CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED
    },
    activeCertification() {
      return (
        (this.certificationVersion && this.certificationVersion.certification) ||
        {}
      )
    },
    certificationId() {
      return this.activeCertification.id || null
    },
    // The backend requires a change note for identity/badge changes based on
    // the parent Certification status, not the version status.
    isParentCertificationPublished() {
      return (
        this.activeCertification.status ===
        CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED
      )
    },
    badgePreviewUrl() {
      return (
        this.localBadgePreviewUrl ||
        this.activeCertification.logo_image_url ||
        ''
      )
    },
    badgeSelectionButtonLabel() {
      return this.selectedBadgeFile ? 'Change badge' : 'Select badge'
    },
    hasPendingBadgeUpload() {
      return Boolean(this.selectedBadgeFile)
    },
    isPersistedPublishedStatus() {
      return this.statusSource === CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED
    },
    statusOptions() {
      if (!this.isAdminUser && this.isPersistedPublishedStatus) {
        return CERTIFICATION_STATUS_OPTIONS.filter(
          (option) => option.value === CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED
        )
      }
      return CERTIFICATION_STATUS_OPTIONS
    },
    publishValidationErrors() {
      return getPublishValidationErrors(
        this.buildPublishValidationCandidate(),
        this.isCreationMode ? {} : this.certificationExam || {}
      )
    },
    canPublishCurrentVersion() {
      return (
        !this.publishValidationErrors.certification.length &&
        !this.publishValidationErrors.exam.length
      )
    },
    publishValidationErrorLabels() {
      return [
        ...mapPublishValidationErrorsToLabels(
          this.publishValidationErrors.certification
        ),
        ...mapPublishValidationErrorsToLabels(
          this.publishValidationErrors.exam
        ),
      ]
    },
    majorVersionImpactWarning() {
      if (this.isCreationMode) return ''
      const majorVersion =
        this.certificationVersion && this.certificationVersion.major_version
      if (!majorVersion) return ''

      return `Changes will impact all ${majorVersion}.x versions.`
    },
    isLeftPanelLoading() {
      return this.isLoading || this.isSaving
    },
    hasPendingStatusChange() {
      return this.status !== this.statusSource
    },
    hasPendingCertificationIdentityChanges() {
      return Object.keys(this.buildCertificationIdentityPayload()).length > 0
    },
    hasPendingCertificationChanges() {
      if (this.isCreationMode) return this.hasQuickCertificationChanges()
      if (!this.certificationVersion || !this.certificationVersion.id)
        return false

      if (this.hasPendingStatusChange) return true
      if (this.hasPendingCertificationIdentityChanges) return true
      if (!this.hasQuickCertificationChanges()) return false

      return this.hasCertificationChanges(
        this.buildCertificationVersionPayload(false)
      )
    },
    hasPendingGeneralInfoChanges() {
      return this.hasPendingCertificationChanges || this.hasPendingBadgeUpload
    },
  },
  watch: {
    isLeftPanelLoading: {
      immediate: true,
      handler(isLoading) {
        if (isLoading) {
          this.$nextTick(() => {
            this.$vs.loading({
              container: '#certification-form-left-panel',
              scale: 0.6,
            })
          })
          return
        }

        this.$vs.loading.close(
          '#certification-form-left-panel > .con-vs-loading'
        )
      },
    },
    certificationVersion: {
      immediate: true,
      handler(certificationVersion) {
        this.clearBadgeRetryStateForDifferentCertification(certificationVersion)
        if (this.shouldFullySyncCertificationVersion(certificationVersion)) {
          this.syncFormFromVersion(certificationVersion)
        } else {
          this.syncSameCertificationVersionRefresh(certificationVersion)
        }
        this.consumePendingBadgeUpload()
      },
    },
    isCreationMode(isCreationMode) {
      if (!isCreationMode) {
        this.syncFormFromVersion(this.certificationVersion)
      }
      this.consumePendingBadgeUpload()
    },
    pendingBadgeUpload: {
      immediate: true,
      handler() {
        this.consumePendingBadgeUpload()
      },
    },
  },
  async created() {
    if (!Object.keys(this.getLAreasEntries).length) {
      await this.getAllLAreas()
    }
  },
  methods: {
    buildLearningPathUrlById,
    ...mapActions('lareas', ['getAllLAreas']),
    ...mapActions('certification', [
      'updateCertificationAction',
      'updateCertificationVersionAction',
      'uploadCertificationBadgeAction',
    ]),
    isDirty() {
      return this.hasPendingGeneralInfoChanges
    },
    async copyLearningPathUrl(learningPathId) {
      const learningPathUrl = buildLearningPathUrlById(learningPathId)
      if (
        !learningPathUrl ||
        typeof navigator === 'undefined' ||
        !navigator.clipboard ||
        typeof navigator.clipboard.writeText !== 'function'
      ) {
        this.notifyLearningPathCopyFailure()
        return false
      }

      try {
        await navigator.clipboard.writeText(learningPathUrl)
        return true
      } catch (error) {
        this.notifyLearningPathCopyFailure()
        return false
      }
    },
    notifyLearningPathCopyFailure() {
      this.$vs.notify({
        title: 'Copy failed',
        text: 'Unable to copy Learning Path URL.',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
        time: '3500',
      })
    },
    triggerBadgeFilePicker() {
      const badgeFileInput = this.$refs.badgeFileInput
      if (!badgeFileInput) return

      badgeFileInput.value = ''
      badgeFileInput.click()
    },
    restoreSelectedBadgeFile(file, certificationId = null) {
      if (!file) return
      if (this.selectedBadgeFile === file) {
        this.badgeOwnerCertificationId = certificationId
        return
      }

      this.badgeUploadError = ''
      this.revokeLocalBadgePreviewUrl()
      this.selectedBadgeFile = file
      this.badgeOwnerCertificationId = certificationId
      this.localBadgePreviewUrl =
        typeof URL.createObjectURL === 'function'
          ? URL.createObjectURL(file)
          : ''
    },
    clearBadgeRetryStateForDifferentCertification(certificationVersion) {
      const certificationId =
        certificationVersion && certificationVersion.certification
          ? certificationVersion.certification.id
          : null
      if (
        !this.badgeOwnerCertificationId ||
        this.badgeOwnerCertificationId === certificationId
      ) {
        return
      }

      this.badgeUploadRequestId += 1
      this.revokeLocalBadgePreviewUrl()
      this.selectedBadgeFile = null
      this.badgeUploadError = ''
      this.isBadgeUploading = false
      this.badgeOwnerCertificationId = null
    },
    consumePendingBadgeUpload() {
      const pendingBadgeUpload = this.pendingBadgeUpload
      const file = pendingBadgeUpload && pendingBadgeUpload.file
      if (!file) {
        this.consumedPendingBadgeUpload = null
        return
      }

      const certificationId =
        this.certificationVersion && this.certificationVersion.certification
          ? this.certificationVersion.certification.id
          : null
      if (
        !certificationId ||
        pendingBadgeUpload.certificationId !== certificationId
      ) {
        return
      }

      if (this.isCreationMode || this.consumedPendingBadgeUpload === pendingBadgeUpload)
        return

      this.consumedPendingBadgeUpload = pendingBadgeUpload
      this.restoreSelectedBadgeFile(file, certificationId)
      this.$emit('pending-badge-consumed')
      this.uploadSelectedBadge(certificationId)
    },
    async uploadSelectedBadge(certificationId, changeNote = null) {
      if (this.isBadgeUploading || !this.selectedBadgeFile || !certificationId) {
        return null
      }

      const file = this.selectedBadgeFile
      this.badgeOwnerCertificationId = certificationId
      const requestId = this.badgeUploadRequestId + 1
      this.badgeUploadRequestId = requestId
      this.isBadgeUploading = true
      this.badgeUploadError = ''
      try {
        const response = await this.uploadCertificationBadgeAction({
          certificationId,
          file,
          ...(changeNote ? { changeNote } : {}),
        })
        if (this.isCurrentBadgeUpload(requestId, file, certificationId)) {
          this.selectedBadgeFile = null
          this.revokeLocalBadgePreviewUrl()
          this.badgeOwnerCertificationId = null
        }
        return response
      } catch (error) {
        if (this.isCurrentBadgeUpload(requestId, file, certificationId)) {
          this.setBadgeUploadErrorFromError(error)
        }
        return null
      } finally {
        if (this.badgeUploadRequestId === requestId) {
          this.isBadgeUploading = false
        }
      }
    },
    isCurrentBadgeUpload(requestId, file, certificationId) {
      return (
        this.badgeUploadRequestId === requestId &&
        this.selectedBadgeFile === file &&
        this.badgeOwnerCertificationId === certificationId
      )
    },
    onBadgeFileSelected(event) {
      if (this.isBadgeUploading || this.isCreatingCertification) return

      const [file] = event.target.files || []
      if (!file) return

      const certificationId =
        this.certificationVersion && this.certificationVersion.certification
          ? this.certificationVersion.certification.id
          : null
      this.restoreSelectedBadgeFile(file, certificationId)
    },
    setBadgeUploadErrorFromError(error) {
      const fileErrors =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.file
      this.badgeUploadError =
        Array.isArray(fileErrors) && fileErrors.length
          ? fileErrors[0]
          : 'Unable to upload certification badge.'
    },
    revokeLocalBadgePreviewUrl() {
      if (!this.localBadgePreviewUrl) return

      if (typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(this.localBadgePreviewUrl)
      }
      this.localBadgePreviewUrl = ''
    },
    setLearningPaths(value) {
      this.form.learningPaths = Array.isArray(value) ? value : []
    },
    buildFormSnapshotFromVersion(certificationVersion) {
      return buildCertificationFormSnapshot(certificationVersion, {
        isCreationMode: this.isCreationMode,
      })
    },
    addLearningPathOptions(learningPaths) {
      learningPaths.forEach((learningPath) => {
        const learningPathId =
          learningPath && typeof learningPath === 'object'
            ? learningPath.id
            : learningPath
        if (
          learningPathId === undefined ||
          learningPathId === null ||
          learningPathId === '' ||
          this.lpathsOptions.some((option) => option.value === learningPathId)
        )
          return

        this.lpathsOptions.push({
          label:
            learningPath && typeof learningPath === 'object'
              ? learningPath.name || learningPathId
              : learningPathId,
          value: learningPathId,
        })
      })
    },
    syncFormFromVersion(certificationVersion) {
      const source = this.isCreationMode ? {} : certificationVersion || {}
      const persistedVersion = buildPersistedCertificationVersion(source)

      this.persistedCertificationVersion = persistedVersion
      this.addLearningPathOptions(persistedVersion.learning_paths)
      this.form = this.buildFormSnapshotFromVersion(source)
      this.status = this.statusSource
      this.originalChangeSnapshot = this.buildChangeSnapshot()
      this.modified = source.modified || ''
      this.created = source.created || ''
      this.createdByName = source.created_by_name || ''
      this.modifiedByName = source.modified_by_name || ''
    },
    getCertificationVersionIdentity(certificationVersion) {
      const certification =
        certificationVersion && certificationVersion.certification
          ? certificationVersion.certification
          : {}

      return {
        certificationVersionId:
          certificationVersion && certificationVersion.id
            ? certificationVersion.id
            : null,
        certificationId: certification.id || null,
      }
    },
    hasSameCertificationVersionIdentity(certificationVersion) {
      const previousIdentity = this.getCertificationVersionIdentity(
        this.persistedCertificationVersion
      )
      const incomingIdentity = this.getCertificationVersionIdentity(
        certificationVersion
      )

      return (
        Boolean(previousIdentity.certificationVersionId) &&
        Boolean(previousIdentity.certificationId) &&
        previousIdentity.certificationVersionId ===
          incomingIdentity.certificationVersionId &&
        previousIdentity.certificationId === incomingIdentity.certificationId
      )
    },
    shouldFullySyncCertificationVersion(certificationVersion) {
      return (
        this.isCreationMode ||
        !this.persistedCertificationVersion ||
        !this.originalChangeSnapshot ||
        !this.hasSameCertificationVersionIdentity(certificationVersion)
      )
    },
    syncSameCertificationVersionRefresh(certificationVersion) {
      const previousSnapshot = {
        ...this.originalChangeSnapshot,
        learningPaths: normalizeCertificationLearningPaths(
          this.originalChangeSnapshot.learningPaths
        ),
      }
      const previousStatus = this.statusSource
      const incomingPersistedVersion = buildPersistedCertificationVersion(
        certificationVersion
      )
      const incomingSnapshot = this.buildFormSnapshotFromVersion(
        certificationVersion
      )
      const nextForm = { ...this.form }

      Object.keys(incomingSnapshot).forEach((key) => {
        if (
          areCertificationFormSnapshotValuesEqual(
            this.form[key],
            previousSnapshot[key]
          )
        ) {
          nextForm[key] = incomingSnapshot[key]
        }
      })

      this.addLearningPathOptions(incomingPersistedVersion.learning_paths)
      this.form = nextForm
      if (this.status === previousStatus) {
        this.status =
          incomingPersistedVersion.status ||
          CONSTANTS.CERTIFICATION_STATUSES.DRAFT
      }
      this.persistedCertificationVersion = incomingPersistedVersion
      this.originalChangeSnapshot = incomingSnapshot
      this.modified = incomingPersistedVersion.modified || ''
      this.created = incomingPersistedVersion.created || ''
      this.createdByName = incomingPersistedVersion.created_by_name || ''
      this.modifiedByName = incomingPersistedVersion.modified_by_name || ''
    },
    buildChangeSnapshot() {
      return buildCertificationFormChangeSnapshot(this.form)
    },
    hasQuickCertificationChanges() {
      if (!this.originalChangeSnapshot) return false

      const currentSnapshot = this.buildChangeSnapshot()

      return Object.keys(currentSnapshot).some(
        (key) =>
          !areCertificationFormSnapshotValuesEqual(
            currentSnapshot[key],
            this.originalChangeSnapshot[key]
          )
      )
    },
    validateCreationIdentityFields() {
      if (!this.isCreationMode) return true

      const errors = getCertificationIdentityValidationErrors({
        name: this.form.certificationName,
        shortName: this.form.certificationShortName,
        learningArea: this.form.learningArea,
      })

      this.certificationIdentityFieldErrors = errors
      return !Object.keys(errors).length
    },
    clearLearningAreaError(value) {
      if (
        !this.isCertificationIdentityEditable ||
        !String(value === undefined || value === null ? '' : value).trim() ||
        !this.certificationIdentityFieldErrors.learningArea
      ) {
        return
      }

      const errors = { ...this.certificationIdentityFieldErrors }
      delete errors.learningArea
      this.certificationIdentityFieldErrors = errors
    },
    validateEditIdentityFields() {
      if (!this.isCertificationIdentityEditable) return true

      const errors = getCertificationIdentityValidationErrors({
        name: this.form.certificationName,
        shortName: this.form.certificationShortName,
        learningArea: this.form.learningArea,
      })

      this.replaceCertificationIdentityErrors(errors)
      return !Object.keys(errors).length
    },
    replaceCertificationIdentityErrors(errors = {}) {
      this.certificationIdentityFieldErrors = errors
    },
    setCertificationIdentityErrors(errors = {}) {
      if (!Object.keys(errors).length) {
        this.certificationIdentityFieldErrors = {}
        return
      }

      this.certificationIdentityFieldErrors = {
        ...this.certificationIdentityFieldErrors,
        ...errors,
      }
    },
    mapCertificationIdentityBackendErrors(error) {
      return mapIdentityBackendErrors(error)
    },
    validateNumericVoucherFields() {
      const errors = {}

      CERTIFICATION_VOUCHER_FIELD_MAPPINGS.forEach(({ formKey, errorKey }) => {
        const value = this.form[formKey]
        if (value === undefined || value === null || value === '') {
          if (this.isCreationMode) return
          errors[errorKey] = 'This field is required and must be a number.'
          return
        }

        const normalized = Number(value)
        if (
          Number.isNaN(normalized) ||
          !Number.isInteger(normalized) ||
          normalized <= 0
        ) {
          errors[errorKey] = 'This field must be a positive integer.'
        }
      })

      this.setVoucherFieldErrors(errors)
      return !Object.keys(errors).length
    },
    buildCertificationVersionPayload(includeStatus = true) {
      const source = this.isCreationMode
        ? {}
        : this.persistedCertificationVersion || this.certificationVersion || {}
      return buildCertificationVersionUpdatePayload({
        certificationVersion: source,
        form: this.form,
        status: this.status,
        includeStatus,
      })
    },
    buildCertificationIdentityPayload() {
      const certification =
        this.certificationVersion && this.certificationVersion.certification
      if (
        this.isCreationMode ||
        !this.isCertificationIdentityEditable ||
        !certification ||
        !certification.id ||
        !this.originalChangeSnapshot
      ) {
        return {}
      }

      return buildCertificationIdentityPatch(
        this.form,
        this.originalChangeSnapshot
      )
    },
    getResolvedResourceValues(response) {
      if (!response) return {}
      return response.data || response
    },
    hasResolvedValue(source, key) {
      return Object.prototype.hasOwnProperty.call(source, key)
    },
    getResolvedValue(source, key, fallback) {
      return this.hasResolvedValue(source, key) ? source[key] : fallback
    },
    shouldSyncCanonicalFormValue(
      hasCanonicalValue,
      currentValue,
      submittedValue
    ) {
      return (
        hasCanonicalValue &&
        areCertificationFormSnapshotValuesEqual(currentValue, submittedValue)
      )
    },
    markCertificationIdentityChangesSaved(payload, response) {
      const resolvedCertification = this.getResolvedResourceValues(response)
      const nextSnapshot = { ...this.originalChangeSnapshot }

      if (this.hasResolvedValue(payload, 'name')) {
        const hasCanonicalValue = this.hasResolvedValue(
          resolvedCertification,
          'name'
        )
        const value = this.getResolvedValue(
          resolvedCertification,
          'name',
          payload.name
        )
        if (
          this.shouldSyncCanonicalFormValue(
            hasCanonicalValue,
            this.form.certificationName,
            payload.name
          )
        ) {
          this.form.certificationName = value
        }
        nextSnapshot.certificationName = value
      }
      if (this.hasResolvedValue(payload, 'short_name')) {
        const hasCanonicalValue = this.hasResolvedValue(
          resolvedCertification,
          'short_name'
        )
        const value = this.getResolvedValue(
          resolvedCertification,
          'short_name',
          payload.short_name
        )
        if (
          this.shouldSyncCanonicalFormValue(
            hasCanonicalValue,
            this.form.certificationShortName,
            payload.short_name
          )
        ) {
          this.form.certificationShortName = value
        }
        nextSnapshot.certificationShortName = value
      }
      if (this.hasResolvedValue(payload, 'learning_area_id')) {
        const hasCanonicalValue = this.hasResolvedValue(
          resolvedCertification,
          'learning_area_id'
        )
        const value = this.getResolvedValue(
          resolvedCertification,
          'learning_area_id',
          payload.learning_area_id
        )
        if (
          this.shouldSyncCanonicalFormValue(
            hasCanonicalValue,
            this.form.learningArea,
            payload.learning_area_id
          )
        ) {
          this.form.learningArea = value
        }
        nextSnapshot.learningArea = value
      }
      if (this.hasResolvedValue(payload, 'difficulty')) {
        const hasCanonicalValue = this.hasResolvedValue(
          resolvedCertification,
          'difficulty'
        )
        const value = this.getResolvedValue(
          resolvedCertification,
          'difficulty',
          payload.difficulty
        )
        if (
          this.shouldSyncCanonicalFormValue(
            hasCanonicalValue,
            this.form.difficulty,
            payload.difficulty
          )
        ) {
          this.form.difficulty = value
        }
        nextSnapshot.difficulty = value
      }

      this.originalChangeSnapshot = nextSnapshot
    },
    markCertificationVersionChangesSaved(payload, response) {
      const requestedChanges = adaptPatchCertificationVersionToApi(payload)
      const form = payload.form || {}
      const resolvedVersion = this.getResolvedResourceValues(response)
      const source =
        this.persistedCertificationVersion || this.certificationVersion || {}
      const sourceVoucherConfig = source.voucher_config || {}
      const resolvedVoucherConfig = resolvedVersion.voucher_config || {}
      const nextVersion = { ...source }
      const nextSnapshot = { ...this.originalChangeSnapshot }

      if (this.hasResolvedValue(requestedChanges, 'status')) {
        const hasCanonicalValue = this.hasResolvedValue(resolvedVersion, 'status')
        const value = this.getResolvedValue(
          resolvedVersion,
          'status',
          form.status
        )
        if (
          this.shouldSyncCanonicalFormValue(
            hasCanonicalValue,
            this.status,
            form.status
          )
        ) {
          this.status = value
        }
        nextVersion.status = value
      }

      if (this.hasResolvedValue(requestedChanges, 'voucher_config')) {
        const nextVoucherConfig = { ...sourceVoucherConfig }
        const requestedVoucherConfig = requestedChanges.voucher_config || {}

        VOUCHER_KEYS.forEach((key) => {
          if (!this.hasResolvedValue(requestedVoucherConfig, key)) return

          const hasCanonicalValue = this.hasResolvedValue(
            resolvedVoucherConfig,
            key
          )
          const value = this.getResolvedValue(
            resolvedVoucherConfig,
            key,
            form[key]
          )
          nextVoucherConfig[key] = value

          if (key === 'max_attempts') {
            if (
              this.shouldSyncCanonicalFormValue(
                hasCanonicalValue,
                this.form.maxAttempts,
                stringifyCertificationFormValue(form.max_attempts)
              )
            ) {
              this.form.maxAttempts = stringifyCertificationFormValue(value)
            }
            nextSnapshot.maxAttempts = stringifyCertificationFormValue(value)
          }
          if (key === 'time_after_first_attempt_in_minutes') {
            if (
              this.shouldSyncCanonicalFormValue(
                hasCanonicalValue,
                this.form.nextAttemptTime,
                stringifyCertificationFormValue(
                  convertMinutesToHours(form.time_after_first_attempt_in_minutes)
                )
              )
            ) {
              this.form.nextAttemptTime = stringifyCertificationFormValue(
                convertMinutesToHours(value)
              )
            }
            nextSnapshot.nextAttemptTime = stringifyCertificationFormValue(
              convertMinutesToHours(value)
            )
          }
          if (key === 'voucher_start_date') {
            if (
              this.shouldSyncCanonicalFormValue(
                hasCanonicalValue,
                this.form.voucherStartDate,
                normalizeCertificationFormDate(form.voucher_start_date)
              )
            ) {
              this.form.voucherStartDate = normalizeCertificationFormDate(value)
            }
            nextSnapshot.voucherStartDate = normalizeCertificationFormDate(value)
          }
          if (key === 'valid_for_in_days') {
            if (
              this.shouldSyncCanonicalFormValue(
                hasCanonicalValue,
                this.form.validForInDays,
                stringifyCertificationFormValue(form.valid_for_in_days)
              )
            ) {
              this.form.validForInDays = stringifyCertificationFormValue(value)
            }
            nextSnapshot.validForInDays = stringifyCertificationFormValue(value)
          }
        })

        nextVersion.voucher_config = nextVoucherConfig
      }

      if (this.hasResolvedValue(requestedChanges, 'accredible_group_id')) {
        const hasCanonicalValue = this.hasResolvedValue(
          resolvedVersion,
          'accredible_group_id'
        )
        const value = this.getResolvedValue(
          resolvedVersion,
          'accredible_group_id',
          form.accredible_group_id
        )
        if (
          this.shouldSyncCanonicalFormValue(
            hasCanonicalValue,
            this.form.accredibleGroupId,
            stringifyCertificationFormValue(form.accredible_group_id)
          )
        ) {
          this.form.accredibleGroupId = stringifyCertificationFormValue(value)
        }
        nextVersion.accredible_group_id = value
        nextSnapshot.accredibleGroupId = stringifyCertificationFormValue(value)
      }
      if (this.hasResolvedValue(requestedChanges, 'retires_at')) {
        const hasCanonicalValue = this.hasResolvedValue(
          resolvedVersion,
          'retires_at'
        )
        const value = this.getResolvedValue(
          resolvedVersion,
          'retires_at',
          form.retires_at
        )
        if (
          this.shouldSyncCanonicalFormValue(
            hasCanonicalValue,
            this.form.retiresAt,
            normalizeCertificationDateInputValue(form.retires_at)
          )
        ) {
          this.form.retiresAt = normalizeCertificationDateInputValue(value)
        }
        nextVersion.retires_at = value
        nextSnapshot.retiresAt = normalizeCertificationDateInputValue(value)
      }
      if (this.hasResolvedValue(requestedChanges, 'learning_paths')) {
        const hasCanonicalValue = this.hasResolvedValue(
          resolvedVersion,
          'learning_paths'
        )
        const value = this.getResolvedValue(
          resolvedVersion,
          'learning_paths',
          form.learning_paths
        )
        if (
          this.shouldSyncCanonicalFormValue(
            hasCanonicalValue,
            this.form.learningPaths,
            normalizeCertificationLearningPaths(form.learning_paths)
          )
        ) {
          this.form.learningPaths = normalizeCertificationLearningPaths(value)
        }
        nextVersion.learning_paths = normalizeCertificationLearningPaths(value)
        nextSnapshot.learningPaths = normalizeCertificationLearningPaths(value)
      }
      if (this.hasResolvedValue(requestedChanges, 'recommendations_md')) {
        const hasCanonicalValue = this.hasResolvedValue(
          resolvedVersion,
          'recommendations_md'
        )
        const value = this.getResolvedValue(
          resolvedVersion,
          'recommendations_md',
          form.recommendations_md
        )
        if (
          this.shouldSyncCanonicalFormValue(
            hasCanonicalValue,
            normalizeCertificationMarkdownValue(this.form.recommendationsMd),
            normalizeCertificationMarkdownValue(form.recommendations_md)
          )
        ) {
          this.form.recommendationsMd = normalizeCertificationMarkdownValue(value)
        }
        nextVersion.recommendations_md = value
        nextSnapshot.recommendationsMd = normalizeCertificationMarkdownValue(value)
      }
      if (this.hasResolvedValue(requestedChanges, 'instructions_md')) {
        const hasCanonicalValue = this.hasResolvedValue(
          resolvedVersion,
          'instructions_md'
        )
        const value = this.getResolvedValue(
          resolvedVersion,
          'instructions_md',
          form.instructions_md
        )
        if (
          this.shouldSyncCanonicalFormValue(
            hasCanonicalValue,
            normalizeCertificationMarkdownValue(this.form.instructionsMd),
            normalizeCertificationMarkdownValue(form.instructions_md)
          )
        ) {
          this.form.instructionsMd = normalizeCertificationMarkdownValue(value)
        }
        nextVersion.instructions_md = value
        nextSnapshot.instructionsMd = normalizeCertificationMarkdownValue(value)
      }

      this.persistedCertificationVersion = nextVersion
      this.originalChangeSnapshot = nextSnapshot
    },
    showPartialSaveWarning() {
      this.$vs.notify({
        title: 'Some changes saved',
        text:
          'Some changes were saved. Unsaved changes are still available to retry.',
        iconPack: 'feather',
        icon: 'icon-alert-triangle',
        color: 'warning',
      })
    },
    buildCreationCertificationPayload() {
      return buildCreationCertificationPayload(this.form)
    },
    buildCreationVersionPayload() {
      return buildCreationCertificationVersionPayload(this.form)
    },
    hasCertificationChanges(payload) {
      const normalizedPayload = adaptPatchCertificationVersionToApi(payload)
      return Object.keys(normalizedPayload).length > 0
    },
    buildPayloadWithPersistedStatus(payload) {
      return {
        ...payload,
        form: {
          ...payload.form,
          status: this.statusSource,
        },
      }
    },
    buildPublishValidationCandidate() {
      const certificationVersion = this.isCreationMode
        ? {}
        : this.certificationVersion || {}
      const certification = certificationVersion.certification || {}
      const voucherConfig = certificationVersion.voucher_config || {}

      return {
        ...certificationVersion,
        certification_short_name: this.form.certificationShortName,
        accredible_group_id: this.form.accredibleGroupId,
        learning_paths: normalizeCertificationLearningPaths(
          this.form.learningPaths
        ),
        recommendations_md: this.form.recommendationsMd,
        status: this.status,
        certification: {
          ...certification,
          learning_area_id: this.form.learningArea,
          difficulty: this.form.difficulty,
        },
        voucher_config: {
          ...voucherConfig,
          max_attempts: this.form.maxAttempts,
          time_after_first_attempt_in_minutes: convertHoursToMinutes(
            this.form.nextAttemptTime
          ),
          valid_for_in_days: this.form.validForInDays,
          voucher_start_date: this.form.voucherStartDate,
        },
      }
    },
    buildSaveSuccessText(hasCertificationChanges, hasStatusUpdate) {
      if (hasCertificationChanges && hasStatusUpdate)
        return 'Certification and status saved'
      if (hasStatusUpdate) return 'Status saved'
      return 'Certification saved'
    },
    isMovingToPublished() {
      return this.isPublishingStatus && !this.isPersistedPublishedStatus
    },
    isMovingOutOfPublished() {
      return !this.isPublishingStatus && this.isPersistedPublishedStatus
    },
    shouldRequirePublishedChangeNote(hasCertificationChanges) {
      return (
        this.isMovingToPublished() ||
        this.isMovingOutOfPublished() ||
        (hasCertificationChanges && this.isPersistedPublishedStatus)
      )
    },
    buildPublishValidationWarningText() {
      return `Complete required publishing fields before publishing:\n- ${this.publishValidationErrorLabels.join(
        '\n- '
      )}`
    },
    async saveCertification() {
      if (this.isCreationMode) return
      const hasStatusUpdate = this.isAdminUser && this.hasPendingStatusChange
      const certificationPayload = this.buildCertificationIdentityPayload()
      const hasCertificationIdentityChanges =
        Object.keys(certificationPayload).length > 0
      const payloadToCheck =
        this.buildCertificationVersionPayload(hasStatusUpdate)
      const hasCertificationChanges = this.hasCertificationChanges(
        this.buildPayloadWithPersistedStatus(payloadToCheck)
      )

      const hasChanges = this.hasPendingGeneralInfoChanges
      const previousCertificationIdentityFieldErrors = {
        ...this.certificationIdentityFieldErrors,
      }

      if (!hasChanges) return

      this.setVoucherFieldErrors({})
      if (hasCertificationChanges && !this.validateNumericVoucherFields())
        return
      if (
        hasCertificationIdentityChanges &&
        !this.validateEditIdentityFields()
      ) {
        return
      }

      let hasPersistedChanges = false
      try {
        let statusChangeNote = null

        if (this.isMovingToPublished() && !this.canPublishCurrentVersion) {
          this.$vs.notify({
            title: 'Missing required value(s)',
            text: this.buildPublishValidationWarningText(),
            color: 'warning',
            time: '12000',
          })
          return
        }

        const requiresChangeNote =
          this.certificationChangeNotesEnabled &&
          (hasStatusUpdate ||
            this.shouldRequirePublishedChangeNote(hasCertificationChanges) ||
            (hasCertificationIdentityChanges &&
              this.isParentCertificationPublished) ||
            (this.hasPendingBadgeUpload && this.isParentCertificationPublished))

        if (requiresChangeNote) {
          statusChangeNote = await this.resolveStatusChangeNote(
            hasCertificationChanges,
            requiresChangeNote
          )
          if (statusChangeNote === null) return
        }

        const payloadToSave = {
          ...payloadToCheck,
          ...(statusChangeNote ? { changeNote: statusChangeNote } : {}),
        }

        this.isSaving = hasChanges
        if (hasCertificationIdentityChanges) {
          let updatedCertification = null
          try {
            updatedCertification = await this.updateCertificationAction({
              certificationId: this.certificationVersion.certification.id,
              payload: {
                ...certificationPayload,
                ...(statusChangeNote ? { change_note: statusChangeNote } : {}),
              },
            })
          } catch (error) {
            const identityErrors = this.mapCertificationIdentityBackendErrors(
              error
            )
            if (Object.keys(identityErrors).length) {
              this.setCertificationIdentityErrors(identityErrors)
            } else if (
              Object.keys(previousCertificationIdentityFieldErrors).length
            ) {
              this.replaceCertificationIdentityErrors(
                previousCertificationIdentityFieldErrors
              )
            }
            throw error
          }
          hasPersistedChanges = true
          this.markCertificationIdentityChangesSaved(
            certificationPayload,
            updatedCertification
          )
          this.setCertificationIdentityErrors({})
        }
        if (hasStatusUpdate || hasCertificationChanges) {
          const updatedVersion = await this.updateCertificationVersionAction(
            payloadToSave
          )
          hasPersistedChanges = true
          this.markCertificationVersionChangesSaved(
            payloadToSave,
            updatedVersion
          )
        }

        if (this.hasPendingBadgeUpload) {
          const badgeUploadResult = statusChangeNote
            ? await this.uploadSelectedBadge(
                this.certificationVersion.certification.id,
                statusChangeNote
              )
            : await this.uploadSelectedBadge(
                this.certificationVersion.certification.id
              )
          if (!badgeUploadResult) {
            if (hasPersistedChanges) {
              this.showPartialSaveWarning()
              if (this.certificationChangeNotesEnabled) this.refreshChangeNotes()
            }
            return
          }
          hasPersistedChanges = true
        }

        if (hasChanges) {
          this.$emit('certification-updated')
        }
        if (hasPersistedChanges && this.certificationChangeNotesEnabled)
          this.refreshChangeNotes()

        this.$vs.notify({
          title: 'Success',
          text: this.buildSaveSuccessText(
            hasCertificationChanges,
            hasStatusUpdate
          ),
          iconPack: 'feather',
          icon: 'icon-check',
          color: 'success',
        })
      } catch (error) {
        this.setVoucherFieldErrors(this.mapVoucherBackendErrors(error))
        if (hasPersistedChanges) {
          this.showPartialSaveWarning()
          if (this.certificationChangeNotesEnabled) this.refreshChangeNotes()
        } else {
          this.showError(error)
        }
      } finally {
        this.isSaving = false
        this.$vs.loading.close(
          '#certification-form-left-panel > .con-vs-loading'
        )
      }
    },
    refreshChangeNotes() {
      const accordion = this.$refs.changeNotesAccordion
      if (accordion && typeof accordion.refresh === 'function') {
        accordion.refresh()
      }
    },
    async resolveStatusChangeNote(hasCertificationChanges, requiresChangeNote) {
      if (
        !requiresChangeNote &&
        !this.shouldRequirePublishedChangeNote(hasCertificationChanges)
      ) {
        return undefined
      }

      const changeNote = await this.requestChangeNote()
      return changeNote || null
    },
    setVoucherFieldErrors(errors = {}) {
      this.voucherFieldErrors = errors
    },
    mapVoucherBackendErrors(error) {
      const responseData = error && error.response && error.response.data
      const raw = Array.isArray(responseData) ? responseData.join('\n') : ''
      const match = raw.match(/On instance\['([^']+)'\]/)
      const field = match && match[1]
      if (!field || !VOUCHER_KEYS.includes(field)) return {}
      return {
        [field]: 'Invalid value. This field must be a number.',
      }
    },
  },
  beforeDestroy() {
    this.revokeLocalBadgePreviewUrl()
    this.$vs.loading.close('#certification-form-left-panel > .con-vs-loading')
  },
}
</script>

<style scoped>
.certification-fields .vs-con-textarea {
  width: 100%;
}
.input-error /deep/ input {
  border-color: #ea5455 !important;
}
.certification-form__learning-area-select--error
  /deep/ .vs__dropdown-toggle {
  border-color: #ea5455 !important;
}
.certification-form__learning-paths-field /deep/ .vs__selected-options {
  flex-wrap: wrap;
  min-width: 0;
}
.certification-form__learning-paths-field
  /deep/ .vs__selected-options .vs__selected {
  min-width: 0;
  max-width: 100%;
}
.certification-form__learning-paths-field
  .certification-form__learning-path-selected-option {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.field-error-msg {
  color: #ea5455;
  display: block;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}
.field-hint-msg {
  color: #626262;
  display: block;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}
.certification-form__badge-preview {
  align-items: center;
  border-radius: 0.75rem;
  display: flex;
  height: 8rem;
  justify-content: center;
  overflow: hidden;
  width: 8rem;
}
.certification-form__badge-preview--empty {
  border: 1px dashed #d8d6de;
}
.certification-form__badge-preview span {
  color: #b8c2cc;
  font-size: 2rem;
}
.certification-form__badge-preview img {
  height: 100%;
  object-fit: contain;
  width: 100%;
}
.certification-form__badge-file-input {
  display: none;
}
.certification-form__primary-fields {
  gap: 1.5rem;
}
.certification-form__badge-field {
  flex: 0 0 10rem;
}
.certification-form__badge-field
  /deep/ .certification-form__badge-select.vs-button.vs-button-border {
  padding: 0.5rem !important;
}
.certification-form__identity-fields {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-width: 0;
}
.certification-form__learning-path-links,
.certification-form__learning-path-links * {
  font-size: 10px;
}
.certification-form__learning-path-copy {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  display: inline-flex;
  padding: 0;
}

@media (max-width: 1200px) {
  .certification-form {
    flex-wrap: wrap;
    flex-direction: column;
    width: 100%;
  }

  .certification-form__left-panel,
  .certification-form__right-panel {
    padding-left: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100%;
  }

  .certification-form__right-panel {
    margin-top: 0;
    margin-bottom: 2rem;
    order: -1;
  }
}

@media (max-width: 900px) {
  .certification-form__primary-fields {
    flex-direction: column;
  }
  .certification-form__badge-field {
    flex-basis: auto;
  }
  .certification-form__name-field,
  .certification-form__short-name-field,
  .certification-form__learning-area-field,
  .certification-form__difficulty-field,
  .certification-form__attempts-field,
  .certification-form__next-attempt-time-field,
  .certification-form__valid-for-days-field,
  .certification-form__accredible-group-id-field,
  .certification-form__retires-at-field,
  .certification-form__learning-paths-field {
    padding-left: 0;
    padding-right: 0;
    width: 100%;
  }
}
</style>
